<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { todoComputed, todoMethods } from '@/state/helpers'
import { mapGetters } from 'vuex'
import McBadge from '@/components/McBadge'
import { debounce } from '@/mixin/helper'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McBadge,
  },
  data() {
    return {
      filter: '',
      allList: [],
      filteredFleet: [],
      filteredTransaction: [],
      filteredCompany: [],
      fleetList: [],
      transactionList: [],
      companyList: [],
    }
  },

  computed: {
    ...todoComputed,
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      isNewModule: 'identity/isNewModule',
      isNewSubmodule: 'identity/isNewSubmodule',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    title() {
      return this.$t('master.dataMaster')
    },

    menuLists() {
      return [
        {
          key: 'fleet',
          category: 'fleet',
          title: this.$t('master.kendaraan'),
          description: this.$t('master.textKendaraan'),
          route: 'Master Fleet',
          condition: this.hasAccessAll(['vehicles:read', 'd1a-default-mdkendaraan:read']),
          icon: 'icon-outline_car',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d1a-default-mdkendaraan'),
          },
        },
        {
          key: 'tracking',
          category: 'fleet',
          title: this.$t('master.pelacak'),
          description: this.$t('master.textPelacak'),
          route: 'Master Tracking',
          condition: this.hasAccessAll(['devices:read', 'd2a-default-mdpelacak:read']),
          icon: 'icon-outline_radio',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d2a-default-mdpelacak'),
          },
        },
        {
          key: 'service',
          category: 'fleet',
          title: this.$t('master.maintenance'),
          description: this.$t('master.textMaintenance'),
          route: 'Master Service',
          condition: this.hasAccessAll(['katalog_sparepart:read']),
          icon: 'icon-outline_engine',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d8a-md-katalogsukucadang'),
          },
        },
        {
          key: 'rfid',
          category: 'fleet',
          title: this.$t('master.rfid.title'),
          description: this.$t('master.rfid.subtitle'),
          route: 'Master RFID',
          condition:
            this.hasAccess(['z1k-rfid-md:read', 'z1k-rfid-md:update']) &&
            !this.hasAccess(['d10a-default-rfid&fr:read', 'd10a-default-rfid&fr:update']),
          icon: 'icon-outline_sensor-rfid',
        },
        {
          key: 'ibeacon',
          category: 'fleet',
          title: this.$t('master.ibeacon.title'),
          description: this.$t('master.ibeacon.subtitle'),
          route: 'Master Ibeacon',
          condition: this.hasAccess(['z1o-ibeacon-md:read', 'z1o-ibeacon-md:update']),
          icon: 'icon-fill_wifi',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('z1o-ibeacon'),
          },
        },
        {
          key: 'customer',
          category: 'transaction',
          title: this.$t('master.pelanggan'),
          description: this.$t('master.textPelanggan'),
          route: 'Master Customer',
          condition: this.hasAccessAll(['tms:create', 'tms:read', 'tms:update', 'tms:delete']),
          icon: 'icon-outline_customer',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d6a-default-md-pelanggan'),
          },
        },
        {
          key: 'product',
          category: 'transaction',
          title: this.$t('master.product.title'),
          description: this.$t('master.product.subtitle'),
          route: 'Master Product',
          condition: this.hasAccessAll(['tms:create', 'tms:read', 'tms:update', 'tms:delete']),
          icon: 'icon-outline_cube',
          hasBadge: true,
          badge: {
            condition: this.isNewModule('tms-base-module'),
          },
        },
        {
          key: 'vendor',
          category: 'transaction',
          title: this.$t('master.vendor.title'),
          description: this.$t('master.vendor.subtitle'),
          route: 'Master Vendor',
          condition: this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']),
          icon: 'icon-outline_vendor',
          hasBadge: false,
        },
        {
          key: 'contract',
          category: 'transaction',
          title: this.$t('master.list.contract.title'),
          description: this.$t('master.list.contract.subtitle'),
          route: 'Master Contract',
          condition:
            this.hasAccessAll([
              'tms-customer-contract:create',
              'tms-customer-contract:read',
              'tms-customer-contract:update',
              'tms-customer-contract:delete',
            ]) || this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']),
          icon: 'icon-outline_file-text',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('tms-customer-contract'),
          },
        },
        {
          key: 'rute',
          category: 'transaction',
          title: this.$t('master.rute'),
          description: this.$t('master.textRute'),
          route: 'Master Route',
          condition: this.hasAccessAll(['geofences:read', 'd7a-default-md-route:read']),
          icon: 'icon-outline_pin-history',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d7a-default-md-route:read'),
          },
        },
        {
          key: 'address',
          category: 'company',
          title: this.$t('master.alamat'),
          description: this.$t('master.textAlamat'),
          route: 'Master Address',
          condition: this.hasAccessAll(['geofences:read', 'd3a-default-mdalamat:read']),
          icon: 'icon-outline_pin',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d3a-default-mdalamat'),
          },
        },
        {
          key: 'orthophoto',
          category: 'company',
          title: this.$t('master.orthophoto.title'),
          description: this.$t('master.orthophoto.subtitle'),
          route: 'Master Orthophoto',
          condition: this.hasAccessAll(['j1b-orthophoto-master-data:read']),
          icon: 'icon-outline_pin',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('j1b-orthophoto-master-data'),
          },
        },
        {
          key: 'user',
          category: 'company',
          title: this.$t('master.pengguna'),
          description: this.$t('master.textPengguna'),
          route: 'Master User',
          condition: this.hasAccessAll(['users:read', 'd4a-default-mdpengguna:read']),
          icon: 'icon-outline_person-add',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d4a-default-mdpengguna'),
          },
        },
        {
          key: 'employee',
          category: 'company',
          title: this.$t('master.karyawan'),
          description: this.$t('master.textKaryawan'),
          route: 'Master Employee',
          condition: this.hasAccessAll(['users:read', 'd5a-default-mdkaryawan:read']),
          icon: 'icon-outline_people',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d5a-default-mdkaryawan'),
          },
        },
        {
          key: 'ritage',
          category: 'company',
          title: this.$t('master.ritage'),
          description: this.$t('master.textRitage'),
          route: 'Master Ritage',
          condition: this.hasAccess(['j2a-ritage-master-data:read']),
          icon: 'icon-outline_pin-history',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('j2a-ritage-master-data'),
          },
        },
        {
          key: 'rjm',
          category: 'company',
          title: 'Voice Announcer',
          description: this.$t('rjm.description'),
          route: 'Master RJM',
          condition: this.hasAccessAll(['users:read', 'd9a-default-voice-announcer:read']),
          icon: 'icon-outline_people',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d9a-default-voice-announcer'),
          },
        },
        {
          key: 'rfidAndFacerecognition',
          category: 'fleet',
          title: this.$t('rfidAndFacerecognition.title'),
          description: this.$t('rfidAndFacerecognition.subtitle'),
          route: 'Master RFID and Face Recognition',
          condition: this.hasAccess([
            'd10a-default-rfid&fr:read',
            'd10a-default-rfid&fr:update',
          ]),
          icon: 'icon-outline_fr-default',
          hasBadge: true,
          badge: {
            condition: this.isNewSubmodule('d10a-default-rfid&fr'),
          },
        },
      ]
    },
  },

  watch: {
    '$i18n.locale': function () {
      this.initData()
      this.filter = ''
    },
  },

  mounted() {
    this.initData()
  },

  methods: {
    ...todoMethods,
    initData() {
      this.allList = this.menuLists
      this.filteredFleet = this.fleetList = this.menuLists.filter(
        (item) => item.category === 'fleet' && item.condition
      )

      this.filteredTransaction = this.transactionList = this.menuLists.filter(
        (item) => item.category === 'transaction' && item.condition
      )

      this.filteredCompany = this.companyList = this.menuLists.filter(
        (item) => item.category === 'company' && item.condition
      )
    },

    searchData: debounce(function () {
      this.filteredFleet = this.fleetList.filter(
        (item) =>
          item.title.toLowerCase().includes(this.filter.toLowerCase()) ||
          item.description.toLowerCase().includes(this.filter.toLowerCase())
      )
      this.filteredTransaction = this.transactionList.filter(
        (item) =>
          item.title.toLowerCase().includes(this.filter.toLowerCase()) ||
          item.description.toLowerCase().includes(this.filter.toLowerCase())
      )
      this.filteredCompany = this.companyList.filter(
        (item) =>
          item.title.toLowerCase().includes(this.filter.toLowerCase()) ||
          item.description.toLowerCase().includes(this.filter.toLowerCase())
      )
    }, 500),
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <b-card class="p-2">
      <div class="d-flex justify-content-end">
        <div class="col-3">
          <b-form-input
            v-model="filter"
            type="search"
            :placeholder="$t('global.search') + '...'"
            @input="searchData"
          />
        </div>
      </div>

      <div class="card mc-card mc-card-front">
        <div class="card-body">
          <div v-if="filteredFleet.length > 0" class="my-3">
            <h4 class="mb-3">
              {{ $t('master.fleet') }}
            </h4>
            <b-row class="mc-card__list">
              <b-col
                v-for="fleet in filteredFleet"
                :key="fleet.key"
                sm="12"
                md="6"
                lg="6"
                xl="4"
                xxl="3"
              >
                <b-button
                  v-if="fleet.condition"
                  variant="outline-light"
                  class="d-block w-100 mc-card__list-item clearfix"
                  @click="$router.push({ name: fleet.route })"
                >
                  <i :class="`mc-fa ${fleet.icon}`"> </i>
                  <McBadge
                    v-if="fleet.badge?.condition && fleet.hasBadge"
                    :text="$t('global.new')"
                    variant="danger"
                    size="medium"
                    type="pill"
                    class="text-uppercase float-end"
                  />
                  <div class="mc-card__list-item-content">
                    <h4>{{ fleet.title }}</h4>
                    <p>{{ fleet.description }}</p>
                  </div>
                </b-button>
              </b-col>
            </b-row>
          </div>

          <div v-if="filteredTransaction.length > 0" class="my-3">
            <h4 class="mb-3">
              {{ $t('master.transaction') }}
            </h4>
            <b-row class="mc-card__list">
              <b-col
                v-for="menu in filteredTransaction"
                :key="menu.key"
                sm="12"
                md="6"
                lg="6"
                xl="4"
                xxl="3"
              >
                <b-button
                  v-if="menu.condition"
                  variant="outline-light"
                  class="d-block w-100 mc-card__list-item clearfix"
                  @click="$router.push({ name: menu.route })"
                >
                  <i :class="`mc-fa ${menu.icon}`"> </i>
                  <McBadge
                    v-if="menu.badge?.condition && menu.hasBadge"
                    :text="$t('global.new')"
                    variant="danger"
                    size="medium"
                    type="pill"
                    class="text-uppercase float-end"
                  />
                  <div class="mc-card__list-item-content">
                    <h4>{{ menu.title }}</h4>
                    <p>{{ menu.description }}</p>
                  </div>
                </b-button>
              </b-col>
            </b-row>
          </div>

          <div v-if="filteredCompany.length > 0" class="my-3">
            <h4 class="mb-3">
              {{ $t('master.list.address.company') }}
            </h4>
            <b-row class="mc-card__list">
              <b-col
                v-for="company in filteredCompany"
                :key="company.key"
                sm="12"
                md="6"
                lg="6"
                xl="4"
                xxl="3"
              >
                <b-button
                  v-if="company.condition"
                  variant="outline-light"
                  class="d-block w-100 mc-card__list-item clearfix"
                  @click="$router.push({ name: company.route })"
                >
                  <i :class="`mc-fa ${company.icon}`"> </i>
                  <McBadge
                    v-if="company.badge?.condition && company.hasBadge"
                    :text="$t('global.new')"
                    variant="danger"
                    size="medium"
                    type="pill"
                    class="text-uppercase float-end"
                  />
                  <div class="mc-card__list-item-content">
                    <h4>{{ company.title }}</h4>
                    <p>{{ company.description }}</p>
                  </div>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>
    </b-card>
  </Layout>
</template>

<style lang="scss" scoped>
.mc-card__list-item {
  min-height: 130px !important;
  height: 90%;

  p {
    text-wrap: wrap !important;
  }
}
</style>
